/*
 * Extend product protection: SDK configuration, shared helpers, analytics and the aftermarket modal.
 * Ported from the live theme's extend-configuration, extend-analytics and extend-aftermarket-integration snippets.
 *
 * Loaded by snippets/extend-configuration.liquid straight after the two Extend SDK scripts. Deferred scripts run
 * in document order, so window.Extend and window.ExtendShopify exist by now, and Extend.config() has to run here
 * (not on DOMContentLoaded) so it is done before assets/extend-product-offer.js connects its element.
 *
 * Offers, analytics and the aftermarket modal are USD-only, as on the live theme (integration.enabled).
 */
(() => {
  if (!window.Extend || !window.ExtendShopify) return;

  const EXTEND_STORE_ID = '46df050d-a5b6-4188-8b98-fa1cd1da72e4';
  window.Extend.config({ storeId: EXTEND_STORE_ID, environment: 'production' });

  const integration = {
    enabled: window.Shopify?.currency?.active === 'USD',
    analytics: true,
    writeLogs: false,
    utils: {
      logger: (objToLog) => {
        if (integration.writeLogs) console.log('EXTEND: ', objToLog);
      },
    },
  };
  window.Extend.integration = integration;

  if (integration.enabled && integration.analytics) {
    // Plan id (the plan line's SKU) for a covered product, looked up through the plan's Ref property.
    const getPlanId = (cart, productId) =>
      cart.items.find((item) => item.properties?.Ref && item.properties.Ref === productId)?.sku?.toString();

    // Called with the product id and quantity at the moment it is added to the cart.
    integration.productAnalytics = (productId, quantity) => {
      if (!productId) {
        console.warn('EXTEND: Product id is ' + productId);
        return;
      }
      window.Extend.trackProductAddedToCart({
        productId: productId.toString(),
        productQuantity: parseInt(quantity || 1),
      });
    };

    // Called with the previous and the new cart whenever the cart changes.
    integration.cartAnalytics = (oldCart, newCart) => {
      try {
        if (!oldCart?.items || !newCart?.items) {
          console.warn('EXTEND: corrupt cart objects');
          return;
        }

        if (oldCart.items.length > newCart.items.length) {
          // An item was removed
          oldCart.items.forEach((currentItem) => {
            if (newCart.items.some((newItem) => currentItem.id == newItem.id)) return;

            if (currentItem.vendor === 'Extend' && currentItem.sku && currentItem.properties?.Ref) {
              window.Extend.trackOfferRemovedFromCart({
                productId: currentItem.properties.Ref.toString(),
                planId: currentItem.sku.toString(),
              });
            } else if (currentItem.id) {
              window.Extend.trackProductRemovedFromCart({ productId: currentItem.id.toString() });
            }
          });
        } else if (oldCart.item_count != newCart.item_count) {
          // A quantity changed
          oldCart.items.forEach((currentItem) => {
            newCart.items.forEach((newItem) => {
              if (currentItem.id != newItem.id || currentItem.quantity == newItem.quantity) return;
              if (currentItem.vendor == 'Extend' || !newItem.quantity) return;

              if (window.ExtendShopify.warrantyAlreadyInCart(currentItem.id.toString(), oldCart.items)) {
                window.Extend.trackOfferUpdated({
                  productId: currentItem.id.toString(),
                  planId: getPlanId(newCart, currentItem.id.toString()),
                  updates: {
                    warrantyQuantity: parseInt(newItem.quantity),
                    productQuantity: parseInt(newItem.quantity),
                  },
                });
              } else {
                window.Extend.trackProductUpdated({
                  productId: currentItem.id.toString(),
                  updates: { productQuantity: parseInt(newItem.quantity) },
                });
              }
            });
          });
        }
      } catch (error) {
        console.error('EXTEND: ' + error);
      }
    };
  }

  // Aftermarket: a ?leadToken=… link opens the aftermarket modal and adds the chosen plan to the cart.
  // Outside product and cart pages this file is injected only for a leadToken URL and can run after
  // DOMContentLoaded, so open the modal straight away when the document is already parsed.
  if (integration.enabled) {
    const openAftermarketModal = () => {
      try {
        const params = new URL(document.location).searchParams;
        const leadToken = params.get('leadToken') || params.get('leadtoken');
        if (!leadToken) return;

        window.Extend.aftermarketModal.open({
          leadToken,
          onClose: (plan, product, quantity) => {
            if (!plan || !product) return;
            window.ExtendShopify.addPlanToCart({ plan, product, leadToken, quantity: quantity || 1 }, () => {
              window.location = window.routes?.cart_url ?? '/cart';
            });
          },
        });
      } catch (error) {
        console.error('EXTEND: ' + error);
      }
    };

    if (document.readyState === 'loading') {
      window.addEventListener('DOMContentLoaded', openAftermarketModal);
    } else {
      openAftermarketModal();
    }
  }
})();
